import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const PasswordResetHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Listen for auth state changes in case Supabase handles the verification automatically
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state change in PasswordResetHandler:', event, session);
      if (event === 'SIGNED_IN' && session) {
        console.log('User signed in via auth state change, redirecting to reset password');
        navigate('/reset-password');
      }
    });

    const handlePasswordReset = async () => {
      try {
        // Get all URL parameters from both query string and hash
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = searchParams.get('access_token') || hashParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token') || hashParams.get('refresh_token');
        const type = searchParams.get('type');
        const token = searchParams.get('token');
        
        // Check for errors in the hash fragment
        const error = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        const errorDescription = hashParams.get('error_description');

        console.log('Password reset params:', { accessToken, refreshToken, type, token });
        console.log('Error params:', { error, errorCode, errorDescription });
        console.log('Current URL:', window.location.href);
        console.log('Hash fragment:', window.location.hash);
        console.log('Search params:', Object.fromEntries(searchParams.entries()));
        console.log('Full window.location:', {
          href: window.location.href,
          search: window.location.search,
          hash: window.location.hash,
          pathname: window.location.pathname
        });

        // Handle Supabase errors
        if (error) {
          console.log('Supabase error detected:', { error, errorCode, errorDescription });
          
          if (errorCode === 'otp_expired') {
            setError('The password reset link has expired. Please request a new one.');
            setLoading(false);
            return;
          } else if (errorCode === 'access_denied') {
            setError('Access denied. The password reset link may be invalid or expired. Please request a new one.');
            setLoading(false);
            return;
          } else {
            setError(`Password reset failed: ${errorDescription || error}. Please request a new one.`);
            setLoading(false);
            return;
          }
        }

        // First, try to get the current session in case Supabase already processed it
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession) {
          console.log('Session already exists, redirecting to reset password');
          navigate('/reset-password');
          return;
        }

        // If we have access_token and refresh_token, we can set the session directly
        if (accessToken && refreshToken) {
          console.log('Setting session with provided tokens');
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Error setting session:', error);
            setError(`Failed to authenticate: ${error.message}`);
            setLoading(false);
            return;
          }

          if (data.session) {
            console.log('Session set successfully, redirecting to reset password');
            navigate('/reset-password');
            return;
          }
        }

        // If we have a token and type, try to verify it
        if (token && type === 'recovery') {
          console.log('Attempting to verify recovery token');
          
          // Try different verification methods
          let session = null;
          let error = null;

          // Method 1: Try verifyOtp with token_hash
          try {
            const { data, error: otpError } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'recovery'
            });
            session = data.session;
            error = otpError;
            console.log('verifyOtp result:', { session: !!session, error });
          } catch (e) {
            console.log('verifyOtp failed:', e);
          }

          // Method 2: Try exchangeCodeForSession
          if (!session && token) {
            try {
              const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(token);
              session = data.session;
              error = exchangeError;
              console.log('exchangeCodeForSession result:', { session: !!session, error });
            } catch (e) {
              console.log('exchangeCodeForSession failed:', e);
            }
          }

          if (error) {
            console.error('Error verifying token:', error);
            setError(`Failed to verify token: ${error.message}`);
            setLoading(false);
            return;
          }

          if (session) {
            console.log('Token verified successfully, redirecting to reset password');
            navigate('/reset-password');
            return;
          }
        }

        // Try to handle the URL as a Supabase auth callback
        if (window.location.search.includes('code=') || window.location.hash.includes('access_token=')) {
          console.log('Detected Supabase auth callback, trying to handle it');
          
          // Let Supabase handle the auth callback automatically
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Error getting session from auth callback:', error);
            setError(`Failed to process auth callback: ${error.message}`);
            setLoading(false);
            return;
          }

          if (data.session) {
            console.log('Session found from auth callback, redirecting to reset password');
            navigate('/reset-password');
            return;
          }
        }

        // If we reach here, we couldn't handle the verification
        console.log('No valid verification method found, trying fallback approach');
        
        // Fallback: Just redirect to reset password page and let it handle the session
        // This might work if Supabase has already processed the URL
        console.log('Attempting fallback redirect to reset password page');
        navigate('/reset-password');

      } catch (error) {
        console.error('Error during password reset handling:', error);
        setError(`An error occurred: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    handlePasswordReset();

    return () => {
      subscription.unsubscribe();
    };
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Processing password reset...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">Password Reset Failed</h2>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Request New Reset Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PasswordResetHandler;
